/**
 * Parameter builders for the Current-Week drilldown modal.
 *
 * The modal can pivot on either entity:
 *   - ``schedule`` (POLineSchedule list at ``/purchasing/schedules/``)
 *     — used for window-level tiles (due / open / arrears) and pending
 *   - ``grn`` (GoodsReceiptNote list at ``/purchasing/grns/``) — used for
 *     by-day / OTD-mix segment clicks because the chart bars are
 *     **GRN-keyed by receipt_date with on_time_status**, not schedule rows.
 *     Earlier the drilldown queried schedules by schedule_date which
 *     produced wildly different counts (chart 172 late vs modal 505
 *     "delivered") and had no on_time_status filter — fixed here.
 *
 * Every mode applies:
 *   - dashboard scope (customer / supplier / part / erp_order_type)
 *   - exclude_line_zero=true (line-0 anchors never count toward GRN-day
 *     buckets or schedule lists)
 */

export type CurrentWeekDrilldownMode =
  | 'due_this_week'      // Cost — every schedule_date in window (denominator)
  | 'open_this_week'     // Cost / Open Schedule Lines — outstanding=true
  | 'received_this_week' // Cost / Received — status=shipped (proxy)
  | 'arrears'            // Cost / Arrears — past-due AND open
  | 'otif_this_week'     // Delivery / OTIF — past-due in OTIF window
  | 'otd_this_week'      // Delivery / OTD  — past-due in OTIF window
  | 'day_on_time'
  | 'day_early'
  | 'day_late'
  | 'day_pending'
  | 'mix_on_time'
  | 'mix_early'
  | 'mix_late'
  | 'mix_otif_pass'         // OTIF — passed (in-full + within tolerance)
  | 'mix_otif_pending'      // OTIF — pending (still within tolerance window)
  | 'mix_otif_fail';        // OTIF — failed (past window without passing,
                            //               or delivered but didn't meet OTIF)

export interface CurrentWeekDrilldownFilters {
  customer_id?: number | string;
  supplier_id?: number | string;
  part_number?: string;
  erp_order_type: '1' | '4';
}

export interface BuildParamsArgs {
  mode: CurrentWeekDrilldownMode;
  filters: CurrentWeekDrilldownFilters;
  weekStartIso: string;
  weekEndIso: string;
  otifStartIso: string;
  otifEndIso: string;
  todayIso: string;
  dayIso?: string;
}

export const MODE_TITLES: Record<CurrentWeekDrilldownMode, string> = {
  due_this_week:      'Schedules due this week',
  open_this_week:     'Open schedule lines this week',
  received_this_week: 'Received schedules this week',
  arrears:            'Arrears — past-due open schedules',
  otif_this_week:     'OTIF — past-due schedules in window',
  otd_this_week:      'OTD — past-due schedules in window',
  day_on_time:        'On-time deliveries',
  day_early:          'Early deliveries',
  day_late:           'Late deliveries',
  day_pending:        'Pending schedules',
  mix_on_time:        'On-time deliveries — week to date',
  mix_early:          'Early deliveries — week to date',
  mix_late:           'Late deliveries — week to date',
  mix_otif_pass:    'OTIF passed — schedules delivered in-full within tolerance',
  mix_otif_pending: 'OTIF pending — still within late-tolerance window',
  mix_otif_fail:    'OTIF failed — past window without passing, or delivered short / out of window',
};

export type DrilldownEntity = 'schedule' | 'grn';

/** Which entity the modal queries for a given mode. ``day_late``,
 *  ``day_early``, ``day_on_time`` and the ``mix_*`` slices match the bar
 *  chart whose source is GoodsReceiptNote bucketed by ``receipt_date``
 *  + ``on_time_status``, so the drilldown queries GRNs directly. Pending
 *  and window/cost tiles stay schedule-keyed. */
export function getEntityForMode(mode: CurrentWeekDrilldownMode): DrilldownEntity {
  switch (mode) {
    case 'day_on_time':
    case 'day_early':
    case 'day_late':
    case 'mix_on_time':
    case 'mix_early':
    case 'mix_late':
      return 'grn';
    default:
      return 'schedule';
  }
}

export type DrilldownParamShape = Record<string, string | number | boolean>;

export function buildCurrentWeekParams(args: BuildParamsArgs): DrilldownParamShape {
  const {
    mode, filters, weekStartIso, weekEndIso, otifStartIso, otifEndIso,
    todayIso, dayIso,
  } = args;
  const entity = getEntityForMode(mode);

  // GRN endpoint uses different filter prefixes than the schedule endpoint.
  // For day_late / day_early / day_on_time / mix_*, query the GRN list
  // (``/purchasing/grns/``) so the drilldown matches the bar chart which
  // counts GRNs by ``receipt_date`` + ``on_time_status``.
  if (entity === 'grn') {
    const g: DrilldownParamShape = {
      po_line__po__erp_order_type: filters.erp_order_type,
    };
    if (filters.customer_id) g['po_line__po__customer'] = filters.customer_id;
    if (filters.supplier_id) g['po_line__po__supplier'] = filters.supplier_id;
    if (filters.part_number) g['po_line__part_number'] = filters.part_number;

    // Filter by on_time_status to match the clicked segment.
    if (mode === 'day_on_time' || mode === 'mix_on_time') g['on_time_status'] = 'on_time';
    else if (mode === 'day_early' || mode === 'mix_early')     g['on_time_status'] = 'early';
    else if (mode === 'day_late'  || mode === 'mix_late')      g['on_time_status'] = 'late';

    // Date axis = receipt_date (matches chart's x-axis).
    if (mode.startsWith('day_') && dayIso) {
      g['receipt_date__gte'] = dayIso;
      g['receipt_date__lte'] = dayIso;
    } else {
      // mix_* — week-to-date OTD window
      g['receipt_date__gte'] = otifStartIso;
      g['receipt_date__lte'] = otifEndIso;
    }
    return g;
  }

  const p: DrilldownParamShape = {
    exclude_line_zero: true,
    po_line__po__erp_order_type: filters.erp_order_type,
  };
  if (filters.customer_id) p['po_line__po__customer'] = filters.customer_id;
  if (filters.supplier_id) p['po_line__po__supplier'] = filters.supplier_id;
  if (filters.part_number) p['po_line__part_number'] = filters.part_number;

  switch (mode) {
    case 'due_this_week':
      p['schedule_date__gte'] = weekStartIso;
      p['schedule_date__lte'] = weekEndIso;
      break;
    case 'open_this_week':
      p['schedule_date__gte'] = weekStartIso;
      p['schedule_date__lte'] = weekEndIso;
      // ``outstanding=true`` matches the tile's open_lines count
      // (schedule_qty − Σ allocated > 0) — what the SQL counts.
      p['outstanding'] = true;
      break;
    case 'received_this_week':
      p['schedule_date__gte'] = weekStartIso;
      p['schedule_date__lte'] = weekEndIso;
      // Tile counts schedules with shipped_qty > 0 in window. Backend
      // filterset accepts shipped_qty__gt directly — count matches the
      // tile, not just a status proxy.
      p['shipped_qty__gt'] = 0;
      break;
    case 'arrears':
      // Tile arrears: schedule_date < today AND balance_qty > 0 (the
      // dashboard's is_past_due predicate is strict `<` — today's
      // not-yet-shipped lines are "due today", not past-due). Use
      // balance_qty__gt=0 (matches the tile CTE column directly)
      // instead of outstanding=true (which recomputes from GRN
      // allocations and diverges when balance_qty is authoritative).
      p['schedule_date__gte'] = weekStartIso;
      p['schedule_date__lt']  = todayIso;
      p['balance_qty__gt'] = 0;
      break;
    case 'otif_this_week':
    case 'otd_this_week':
      // Past-due in the OTIF/OTD window (Sun → Thu/today).
      // Tile metric is schedule-line-based on past-due rows. List can't
      // recreate in_full / within_firm exactly; we drill into the
      // denominator (total_due) and let the user inspect rows.
      p['schedule_date__gte'] = otifStartIso;
      p['schedule_date__lte'] = todayIso;
      break;
    case 'day_pending':
      // Schedules with outstanding balance for this day.
      //
      // The chart's "pending" series rolls all past-due (Sun→today)
      // schedules into today's bar, so clicking today opens the full
      // arrears+today bucket; clicking any future day shows only that
      // day. Mirrors the SQL in ``_deliveries_by_day``.
      if (dayIso && dayIso === todayIso) {
        p['schedule_date__gte'] = weekStartIso;
        p['schedule_date__lte'] = todayIso;
      } else if (dayIso) {
        p['schedule_date__gte'] = dayIso;
        p['schedule_date__lte'] = dayIso;
      } else {
        p['schedule_date__gte'] = weekStartIso;
        p['schedule_date__lte'] = weekEndIso;
      }
      p['outstanding'] = true;
      break;
    // day_on_time / day_early / day_late / mix_* are handled above by the
    // GRN entity branch — they never reach this switch.
    case 'day_on_time':
    case 'day_early':
    case 'day_late':
    case 'mix_on_time':
    case 'mix_early':
    case 'mix_late':
      // unreachable; fall-through guarded by getEntityForMode().
      break;
    case 'mix_otif_pass':
    case 'mix_otif_pending':
    case 'mix_otif_fail':
      // OTIF Mix — three-state schedule-keyed drilldown across the
      // full-week bucket. Filter by ``otif_status`` to match the slice.
      p['schedule_date__gte'] = weekStartIso;
      p['schedule_date__lte'] = weekEndIso;
      if (mode === 'mix_otif_pass')         p['otif_status'] = 'passed';
      else if (mode === 'mix_otif_pending') p['otif_status'] = 'pending';
      else                                  p['otif_status'] = 'failed';
      break;
  }
  return p;
}
